import React from 'react';
import { Box, Button, Typography, Paper, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import BugReportIcon from '@mui/icons-material/BugReport';
import sessionManager from '../../utils/sessionManager';
import tokenManager from '../../auth/TokenManager';
import logger from '../../utils/logger';

const TestContainer = styled(Container)(({ theme }) => ({
  marginTop: '50px',
  marginBottom: '50px'
}));

const TestPaper = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
}));

const TestButton = styled(Button)(({ theme }) => ({
  margin: '10px',
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 600
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: '20px',
  fontWeight: 700,
  color: theme.palette.primary.main
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  marginBottom: '30px',
  color: theme.palette.text.secondary
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: '30px',
  padding: '20px',
  backgroundColor: theme.palette.grey[50],
  borderRadius: '8px'
}));

/**
 * TestSessionExpired - Testing component for Session Expired Modal
 * This component provides buttons to test the session expiration functionality
 * 
 * To use this component, add it as a route in App.js:
 * <Route path="/test-session-expired" element={<TestSessionExpired />} />
 */
const TestSessionExpired = () => {
  const handleTriggerModal = () => {
    logger.log('🧪 [Test] Triggering session expired modal');
    sessionManager.triggerSessionExpired();
  };

  const handleClearGoogleToken = () => {
    logger.log('🧪 [Test] Clearing Google token');
    tokenManager.clear('google');
    alert('Google token cleared! Try accessing GMB data to see the session expired modal.');
  };

  const handleResetSession = () => {
    logger.log('🧪 [Test] Resetting session expired state');
    sessionManager.resetSessionExpired();
    alert('Session expired state reset! You can now trigger the modal again.');
  };

  const handleCheckTokens = () => {
    logger.log('🧪 [Test] Checking current tokens');
    const googleToken = tokenManager.get('google');
    const backendToken = localStorage.getItem('userSession');
    
    console.log('Current Tokens:', {
      googleToken,
      backendToken: backendToken ? JSON.parse(backendToken) : null
    });
    
    alert('Check the browser console for token details');
  };

  const handleSimulate401 = () => {
    logger.log('🧪 [Test] Simulating 401 error');
    const error = new Error('Unauthorized');
    error.status = 401;
    sessionManager.handleApiError(error);
  };

  const handleSimulateExpiredMessage = () => {
    logger.log('🧪 [Test] Simulating expired token message');
    const error = new Error('Error loading GMB profile: Token expired');
    sessionManager.handleApiError(error);
  };

  return (
    <TestContainer maxWidth="md">
      <TestPaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BugReportIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Title variant="h3">
            Session Expired Modal - Testing
          </Title>
          <Subtitle variant="body1">
            Use the buttons below to test the session expiration functionality
          </Subtitle>
        </Box>

        <Section>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            🎯 Direct Testing
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Directly trigger the session expired modal
          </Typography>
          <TestButton
            variant="contained"
            color="error"
            onClick={handleTriggerModal}
            fullWidth
          >
            Trigger Session Expired Modal
          </TestButton>
        </Section>

        <Section>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            🔑 Token Management
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Clear tokens and check current token state
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TestButton
              variant="outlined"
              color="warning"
              onClick={handleClearGoogleToken}
            >
              Clear Google Token
            </TestButton>
            <TestButton
              variant="outlined"
              color="info"
              onClick={handleCheckTokens}
            >
              Check Current Tokens (Console)
            </TestButton>
          </Box>
        </Section>

        <Section>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            🧪 Simulate Errors
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Simulate different types of session expiration errors
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TestButton
              variant="outlined"
              color="secondary"
              onClick={handleSimulate401}
            >
              Simulate 401 Error
            </TestButton>
            <TestButton
              variant="outlined"
              color="secondary"
              onClick={handleSimulateExpiredMessage}
            >
              Simulate "Token Expired" Message
            </TestButton>
          </Box>
        </Section>

        <Section>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            🔄 Reset
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Reset the session expired state if needed
          </Typography>
          <TestButton
            variant="outlined"
            onClick={handleResetSession}
            fullWidth
          >
            Reset Session Expired State
          </TestButton>
        </Section>

        <Box sx={{ mt: 4, p: 2, backgroundColor: 'info.light', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            💡 Testing Tips:
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>Click "Trigger Session Expired Modal" to see the modal immediately</li>
              <li>Click "Clear Google Token" then try accessing GMB features</li>
              <li>Use "Check Current Tokens" to debug token issues</li>
              <li>After modal appears, you can only close it by clicking "Login Again"</li>
              <li>Check browser console for detailed logs</li>
            </ol>
          </Typography>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            This is a development/testing component. Remove from production builds.
          </Typography>
        </Box>
      </TestPaper>
    </TestContainer>
  );
};

export default TestSessionExpired;

